/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  GeofenceGeometry,
  GeofenceInput,
  GeofenceType,
  GeofenceUpdateInput,
  GeofencePolygonGeometry,
  GeofenceCircleGeometry,
  useGeofenceQueryV1,
  useUpdateGeofenceMutationV1,
  getGeofenceIdentifier,
} from "@/api/v1/geofence-api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GeofenceDraw } from "@/components/map/geofence-draw";
import { GeofenceForm } from "@/components/geofences/geofence-form";
import { toast } from "sonner";
import { Map } from "react-map-gl/maplibre";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Loader2, 
  MapPin, 
  Settings, 
  Info, 
  CheckCircle,
  AlertTriangle
} from "lucide-react";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

export function GeofenceEditRoute() {
  const navigate = useNavigate();
  const params = useParams({ from: "/geofences/$geofenceId/edit" });
  const geofenceId = params.geofenceId;

  const [geofenceGeometry, setGeofenceGeometry] =
    useState<GeofenceGeometry | null>(null);
  const [step, setStep] = useState<"loading" | "draw" | "details">("loading");
  const [geofenceType, setGeofenceType] = useState<GeofenceType | null>(null);

  const { data: geofence, isLoading, isError } = useGeofenceQueryV1(geofenceId);
  const updateGeofenceMutation = useUpdateGeofenceMutationV1();

  useEffect(() => {
    if (geofence) {
      try {
        // In v1 API, geometry is already a PolygonGeometry (number[][][])
        // Backend stores all geometry as polygons, even circles
        let geometry: GeofenceGeometry;

        if (geofence.type === "circle") {
          // For circles, backend stores as polygon approximation
          // We'll treat it as a polygon since we can't reconstruct the original circle
          geometry = {
            type: "polygon",
            coordinates: geofence.geometry,
          } as GeofencePolygonGeometry;
        } else {
          geometry = {
            type: "polygon",
            coordinates: geofence.geometry,
          } as GeofencePolygonGeometry;
        }

        setGeofenceGeometry(geometry);
        setGeofenceType(geofence.type);
        setStep("draw");
      } catch (error) {
        console.error("Failed to load geofence geometry:", error);
        toast.error("Failed to load geofence geometry");
        navigate({ to: "/geofences" as any });
      }
    }
  }, [geofence, navigate]);

  const handleGeofenceDrawn = (
    geometry: GeofenceGeometry,
    type: GeofenceType
  ) => {
    setGeofenceGeometry(geometry);
    setGeofenceType(type);
  };

  const handleContinue = () => {
    if (!geofenceGeometry) {
      toast.error("Please draw a valid geofence");
      return;
    }

    setStep("details");
  };

  const handleSubmit = async (data: GeofenceInput) => {
    if (!geofence || !geofenceGeometry || !geofenceType) return;

    try {
      const updateInput: GeofenceUpdateInput = {
        name: data.name,
        description: data.description || null,
        type: geofenceType,
        geometry: geofenceGeometry,
      };

      // Use slug if available, otherwise use id
      const identifier = getGeofenceIdentifier(geofence);
      
      await updateGeofenceMutation.mutateAsync({
        slug: typeof identifier === "string" && identifier !== "" ? identifier : undefined,
        id: typeof identifier === "number" ? identifier : geofence.id,
        geofenceUpdateInput: updateInput,
      });
      toast.success("Geofence updated successfully");
      navigate({ to: "/geofences" as any });
    } catch (error) {
      console.error("Failed to update geofence:", error);
      toast.error("Failed to update geofence");
    }
  };

  if (isLoading || step === "loading") {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Geofences", href: "/geofences" },
              { label: "Loading..." }
            ]}
            className="mb-6"
          />
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading geofence data...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main>
        <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
          <BreadcrumbNav
            items={[
              { label: "Home", href: "/" },
              { label: "Geofences", href: "/geofences" },
              { label: "Error" }
            ]}
            className="mb-6"
          />
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Failed to load geofence</p>
                <p>The geofence you're trying to edit could not be found or there was an error loading the data.</p>
                <Button onClick={() => navigate({ to: "/geofences" as any })} className="mt-2">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Geofences
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Geofences", href: "/geofences" },
            { label: geofence?.name ?? "Geofence" },
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate({ to: "/geofences" as any })}
              className="h-10 w-10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold">Edit Geofence</h1>
              <p className="text-sm text-muted-foreground">
                Modify {geofence?.name} • Update boundary and settings
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="w-fit">
              <MapPin className="h-3 w-3 mr-1" />
              Step {step === "draw" ? "1" : "2"} of 2
            </Badge>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-4">
          <div className={`flex items-center ${step === "draw" ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "draw" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              {step === "draw" ? "1" : <CheckCircle className="h-4 w-4" />}
            </div>
            <span className="ml-2 text-sm font-medium">Edit Boundary</span>
          </div>
          <div className="flex-1 h-px bg-muted"></div>
          <div className={`flex items-center ${step === "details" ? "text-primary" : "text-muted-foreground"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === "details" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              2
            </div>
            <span className="ml-2 text-sm font-medium">Update Details</span>
          </div>
        </div>

        {step === "draw" ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Edit Geofence Boundary
              </CardTitle>
              <CardDescription>
                Modify the geofence shape on the map. You can drag points to adjust the boundary or create a new shape.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="h-[600px] w-full relative">
                <Map
                  id="geofence-map"
                  mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                  initialViewState={{
                    longitude:
                      geofenceType === "circle"
                        ? ((geofenceGeometry as GeofenceCircleGeometry)
                            ?.center?.[0] ?? -0.1276)
                        : ((geofenceGeometry as GeofencePolygonGeometry)
                            ?.coordinates?.[0]?.[0]?.[0] ?? -0.1276),
                    latitude:
                      geofenceType === "circle"
                        ? ((geofenceGeometry as GeofenceCircleGeometry)
                            ?.center?.[1] ?? 51.5072)
                        : ((geofenceGeometry as GeofencePolygonGeometry)
                            ?.coordinates?.[0]?.[0]?.[1] ?? 51.5072),
                    zoom: 12,
                  }}
                  style={{ width: "100%", height: "100%" }}
                  mapLib={import("maplibre-gl")}
                  attributionControl={false}
                  reuseMaps
                >
                  <GeofenceDraw
                    onGeofenceDrawn={handleGeofenceDrawn}
                    initialGeometry={geofenceGeometry ?? undefined}
                  />
                </Map>
              </div>
            </CardContent>
            <div className="p-4 border-t flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {geofenceGeometry ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span>Boundary updated successfully</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <span>Modify the boundary on the map to continue</span>
                  </div>
                )}
              </div>
              <Button onClick={handleContinue} disabled={!geofenceGeometry}>
                Continue to Details
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
            {/* Geofence Preview */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    Geofence Preview
                  </CardTitle>
                  <CardDescription>
                    Review your updated geofence configuration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium">Name</p>
                    <p className="text-sm text-muted-foreground">
                      {geofence?.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Type</p>
                    <Badge variant="secondary" className="mt-1">
                      {geofenceType}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Geometry</p>
                    <p className="text-sm text-muted-foreground">
                      {geofenceGeometry?.type}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-600">Ready to update</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Configuration Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Update Geofence Configuration
                  </CardTitle>
                  <CardDescription>
                    Modify details and settings for this geofence
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {geofence && geofenceGeometry && (
                    <GeofenceForm
                      onSubmit={handleSubmit}
                      geofenceGeometry={geofenceGeometry}
                      geometryType={geofenceType || "polygon"}
                      initialValues={{
                        name: geofence.name,
                        description: geofence.description ?? undefined,
                      }}
                      isSubmitting={updateGeofenceMutation.isPending}
                    />
                  )}
                  <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setStep("draw")}
                      className="flex-1 sm:flex-none"
                    >
                      Back to Drawing
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Help Information */}
        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">Editing Geofences</p>
              <p>
                {step === "draw" 
                  ? "Modify the geofence boundary by dragging existing points or creating a new shape. The updated boundary will be saved when you proceed to the details step."
                  : "Update your geofence settings including name and description. Changes will be applied when you save the geofence."
                }
              </p>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </main>
  );
}
